
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../../components/docs/ContentSwitcher';
import SampleCode from '../../../../components/docs/plaidoms/SampleCode';
import Actions from '../../../../components/docs/Actions';
import Layout from '../../../../components/docs/Layout';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to use one-time Payment Initiation in your application'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const PaymentInitiationSetupFlow = makeShortcode("PaymentInitiationSetupFlow");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Add One-Time Payment Initiation to your app' subtitle='Learn how to use one-time Payment Initiation in your application' mdxType="Header" />
    <p>{`In this guide, we start from scratch and walk through how to set up a one-time `}<a parentName="p" {...{
        "href": "/docs/api/products/payment-initiation/"
      }}>{`Payment Initiation`}</a>{` flow. For a high-level overview of all our payment related offerings, see the `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/"
      }}>{`Introduction to Payments (Europe)`}</a>{`.`}</p>
    <p>{`For an example guide to using one-time Payment Initiation as part of an integrated onboarding flow with other Plaid products, see `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/payment-initiation-one-time/user-onboarding-and-account-funding/"
      }}>{`Onboarding and account funding guide`}</a>{`.`}</p>
    <Callout mdxType="Callout">
      <p>{`This guide describes the latest integration pattern for one-time Payment Initiation, which uses a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` to represent the sender. A `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` is required for new clients integrating with Payment Initiation. A `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` remains optional for existing integrations. For more information about `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, see `}<a parentName="p" {...{
          "href": "/docs/api/users/"
        }}>{`User APIs`}</a>{`.`}</p>
    </Callout>
    <h4 {...{
      "id": "get-plaid-api-keys-and-complete-application-and-company-profile"
    }}>{`Get Plaid API keys and complete application and company profile`}</h4>
    <p>{`If you don't already have one, you'll need to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup"
      }}>{`create a Plaid developer account`}</a>{`. After creating your account, you can find your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`API keys`}</a>{` under Build in the Plaid Dashboard.`}</p>
    <p>{`You will also need to complete your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/app-branding"
      }}>{`application profile`}</a>{` and `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/profile"
      }}>{`company profile`}</a>{` on the Dashboard. The information in your profile will be shared with users of your application when they manage their connection on the `}<a parentName="p" {...{
        "href": "https://my.plaid.com"
      }}>{`Plaid Portal`}</a>{`, and must be completed before connecting to certain institutions.`}</p>
    <h4 {...{
      "id": "access-payment-initiation"
    }}>{`Access Payment Initiation`}</h4>
    <p>{`Payments (Europe) is enabled in Sandbox by default. It uses test data and does not interact with financial institutions. You may need to request access to Payment Initiation via the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com"
      }}>{`Dashboard`}</a>{` before using it in Production.`}</p>
    <h4 {...{
      "id": "install-and-initialize-plaid-libraries"
    }}>{`Install and initialize Plaid libraries`}</h4>
    <p>{`You can use our official server-side client libraries to connect to the Plaid API from your application:`}</p>
    <SampleCode.InstallClientLibraries />
    <p>{`After you've installed Plaid's client libraries, you can initialize them by passing in your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and the environment you wish to connect to (Sandbox or Production). This will make sure the client libraries pass along your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` with each request, and you won't need to include them in any other calls.`}</p>
    <SampleCode.InitializeClientLibraries />
    <h4 {...{
      "id": "one-time-payment-initiation-flow-overview"
    }}>{`One-time Payment Initiation flow overview`}</h4>
    <p>{`Before we jump into the code, let's see an overview of the steps needed to set up your payment with Plaid:`}</p>
    <PaymentInitiationSetupFlow mdxType="PaymentInitiationSetupFlow" />
    <h5 {...{
      "id": "create-a-recipient"
    }}>{`Create a recipient`}</h5>
    <p>{`Before beginning the payment initiation process, you will need to know the name and account information of the recipient. With this information in hand, you can then call `}<inlineCode parentName="p">{`/payment_initiation/recipient/create`}</inlineCode>{` to create a payment recipient and receive a `}<inlineCode parentName="p">{`recipient_id`}</inlineCode>{`. You can later reuse this `}<inlineCode parentName="p">{`recipient_id`}</inlineCode>{` for future payments to the same account.`}</p>
    <MDXCodeBlock title='/payment_initiation/recipient/create' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const { PaymentInitiationRecipientCreateRequest } = require('plaid');

// Using Bacs, without IBAN or address
const request: PaymentInitiationRecipientCreateRequest = {
  name: 'John Doe',
  bacs: {
    account: '26207729',
    sort_code: '560029',
  },
};
try {
  const response = await plaidClient.paymentInitiationRecipientCreate(request);
  const recipientID = response.data.recipient_id;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/payment_initiation/recipient/create \\
-H 'Content-Type: application/json' \\
-d '{
 "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
 "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
 "name": String,
 "iban": String,
 "bacs": Object,
 "address": Object
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`require 'plaid'

request = Plaid::PaymentInitiationRecipientCreateRequest.new(
 {
 name: "John Doe",
 iban: "GB33BUKB20201555555555",
 address: {
      street: ["Street Name 999"],
      city: "City",
      postal_code: "99999",
      country: "GB",
    },
 bacs: {
      account: "26207729",
      sort_code: "560029",
    }
 }
)
response = client.payment_initiation_recipient_create(request)
recipient_id = response.recipient_id
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.model.PaymentInitiationRecipientCreateRequest;
import com.plaid.client.model.PaymentInitiationRecipientCreateResponse;
import com.plaid.client.model.RecipientBACSNullable;
import com.plaid.client.model.PaymentInitiationAddress;

PaymentInitiationAddress address = new PaymentInitiationAddress()
  .street(Arrays.asList("Street Name 999"))
  .city("City")
  .postalCode("99999")
  .country("GB");
RecipientBACSNullable bacs = new RecipientBACSNullable()
  .account("26207729")
  .sortCode("560029");

PaymentInitiationRecipientCreateRequest request = new PaymentInitiationRecipientCreateRequest()
  .name("John Doe")
  .bacs(bacs)
  .address(address);

Response<PaymentInitiationRecipientCreateResponse> response = client
  .paymentInitiationRecipientCreate(request)
  .execute();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import plaid
from plaid.model.payment_initiation_recipient_create_request import PaymentInitiationRecipientCreateRequest

request = PaymentInitiationRecipientCreateRequest(
    name='John Doe',
    iban='GB33BUKB20201555555555',
    address=PaymentInitiationAddress(
        street=['street name 999'],
        city='city',
        postal_code='99999',
        country='GB'
    )
)
response = client.payment_initiation_recipient_create(request)
recipient_id = response["recipient_id"]
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v48/plaid"
)

request := plaid.NewPaymentInitiationRecipientCreateRequest("John Doe")
request.SetBacs(plaid.RecipientBACSNullable{
 Account:  plaid.PtrString("26207729"),
 SortCode: plaid.PtrString("560029"),
})
request.SetAddress(*plaid.NewPaymentInitiationAddress([]string{"Street Name 999"},
  "City",
  "99999",
  "GB",
))
paymentRecipientCreateResp, _, err := client.PlaidApi.PaymentInitiationRecipientCreate(ctx).PaymentInitiationRecipientCreateRequest(*request).Execute()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v49"
)

request := &plaid.PaymentInitiationRecipientCreateRequest{Name: "John Doe"}
request.Bacs = &plaid.RecipientBacsNullable{
    Account:  plaid.String("26207729"),
    SortCode: plaid.String("560029"),
}
request.Address = &plaid.PaymentInitiationAddress{
    Street:     []string{"Street Name 999"},
    City:       "City",
    PostalCode: "99999",
    Country:    "GB",
}
paymentRecipientCreateResp, err := client.PaymentInitiation.CreateRecipient(ctx, request)
`}</code></pre>
    </MDXCodeBlock>
    <h5 {...{
      "id": "create-a-user"
    }}>{`Create a user`}</h5>
    <p>{`Next, create a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` to represent the sender of the payment. Call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` with a unique `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` — typically the same identifier your own application uses for the currently signed-in user — along with an `}<inlineCode parentName="p">{`identity`}</inlineCode>{` object describing the sender.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`identity`}</inlineCode>{` object must include the sender's `}<inlineCode parentName="p">{`name`}</inlineCode>{`, plus at least one of `}<inlineCode parentName="p">{`emails`}</inlineCode>{` or `}<inlineCode parentName="p">{`phone_numbers`}</inlineCode>{`. Phone numbers should be supplied in `}<a parentName="p" {...{
        "href": "https://wikipedia.org/wiki/E.164"
      }}>{`E.164`}</a>{` format, for example `}<inlineCode parentName="p">{`+441632960123`}</inlineCode>{`. All other fields in the identity object are optional.`}</p>
    <p><inlineCode parentName="p">{`/user/create`}</inlineCode>{` is idempotent: calling it again with the same `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` returns the same `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, so you can reuse a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` for future payments by the same sender. For the full request schema and the other User endpoints, see `}<a parentName="p" {...{
        "href": "/docs/api/users/"
      }}>{`User endpoints`}</a>{`.`}</p>
    <MDXCodeBlock title='/user/create' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const { UserCreateRequest } = require('plaid');

const request: UserCreateRequest = {
  client_user_id: 'user-abc-123',
  identity: {
    name: {
      given_name: 'John',
      family_name: 'Doe',
    },
    emails: [{ data: 'john.doe@example.com', primary: true }],
  },
};
try {
  const response = await plaidClient.userCreate(request);
  const userID = response.data.user_id;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/user/create \\
-H 'Content-Type: application/json' \\
-d '{
 "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
 "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
 "client_user_id": String,
 "identity": Object
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`require 'plaid'

request = Plaid::UserCreateRequest.new(
 {
 client_user_id: "user-abc-123",
 identity: {
      name: {
        given_name: "John",
        family_name: "Doe",
      },
      emails: [{ data: "john.doe@example.com", primary: true }]
    }
 }
)
response = client.user_create(request)
user_id = response.user_id
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.model.ClientUserIdentity;
import com.plaid.client.model.ClientUserIdentityEmail;
import com.plaid.client.model.ClientUserIdentityName;
import com.plaid.client.model.UserCreateRequest;
import com.plaid.client.model.UserCreateResponse;

ClientUserIdentity identity = new ClientUserIdentity()
  .name(new ClientUserIdentityName()
    .givenName("John")
    .familyName("Doe"))
  .emails(Arrays.asList(new ClientUserIdentityEmail()
    .data("john.doe@example.com")
    .primary(true)));

UserCreateRequest request = new UserCreateRequest()
  .clientUserId("user-abc-123")
  .identity(identity);

Response<UserCreateResponse> response = client
  .userCreate(request)
  .execute();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import plaid
from plaid.model.user_create_request import UserCreateRequest
from plaid.model.client_user_identity import ClientUserIdentity
from plaid.model.client_user_identity_name import ClientUserIdentityName
from plaid.model.client_user_identity_email import ClientUserIdentityEmail

request = UserCreateRequest(
    client_user_id='user-abc-123',
    identity=ClientUserIdentity(
        name=ClientUserIdentityName(
            given_name='John',
            family_name='Doe'
        ),
        emails=[ClientUserIdentityEmail(
            data='john.doe@example.com',
            primary=True
        )]
    )
)
response = client.user_create(request)
user_id = response["user_id"]
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v43/plaid"
)

identity := plaid.NewClientUserIdentity()
identity.SetName(*plaid.NewClientUserIdentityName("John", "Doe"))
identity.SetEmails([]plaid.ClientUserIdentityEmail{
 *plaid.NewClientUserIdentityEmail("john.doe@example.com", true),
})

request := plaid.NewUserCreateRequest("user-abc-123")
request.SetIdentity(*identity)

userCreateResp, _, err := client.PlaidApi.UserCreate(ctx).UserCreateRequest(*request).Execute()
userID := userCreateResp.GetUserId()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`request := &plaid.UserCreateRequest{
    ClientUserID: "user-abc-123",
    Identity: &plaid.ClientUserIdentity{
        Name: &plaid.ClientUserIdentityName{GivenName: "John", FamilyName: "Doe"},
        Emails: []*plaid.ClientUserIdentityEmail{
            {Data: "john.doe@example.com", Primary: true},
        },
    },
}

userCreateResp, err := client.User.Create(ctx, request)
userID := userCreateResp.GetUserID()
`}</code></pre>
    </MDXCodeBlock>
    <h5 {...{
      "id": "create-a-payment"
    }}>{`Create a payment`}</h5>
    <p>{`Now that you have the `}<inlineCode parentName="p">{`recipient_id`}</inlineCode>{` and the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, you can provide them together with the payment amount to `}<inlineCode parentName="p">{`/payment_initiation/payment/create`}</inlineCode>{`, which returns a `}<inlineCode parentName="p">{`payment_id`}</inlineCode>{`.`}</p>
    <MDXCodeBlock title='/payment_initiation/payment/create' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v48/plaid"
)

request := plaid.NewPaymentInitiationPaymentCreateRequest(
  recipientID,
  "TestPayment",
  *plaid.NewPaymentAmount("GBP", 100.0),
)
request.SetUserId(userID)

response, _, err := client.PlaidApi.PaymentInitiationPaymentCreate(ctx).PaymentInitiationPaymentCreateRequest(*request).Execute()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v49"
)

request := &plaid.PaymentInitiationPaymentCreateRequest{
    RecipientID: recipientID,
    Reference:   "TestPayment",
    Amount: &plaid.PaymentAmount{
        Currency: "GBP",
        Value:    100.0,
    },
}
request.UserID = &userID

response, err := client.PaymentInitiation.CreatePayment(ctx, request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import plaid
from plaid.model.payment_initiation_payment_create_request import PaymentInitiationPaymentCreateRequest
from plaid.model.payment_amount import PaymentAmount
from plaid.model.payment_amount_currency import PaymentAmountCurrency

request = PaymentInitiationPaymentCreateRequest(
    recipient_id=recipient_id,
    user_id=user_id,
    reference='TestPayment',
    amount=PaymentAmount(
        currency=PaymentAmountCurrency('GBP'),
        value=100.00
    )
)
response = client.payment_initiation_payment_create(request)
payment_id = response["payment_id"]
status = response["status"]
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.model.PaymentInitiationPaymentCreateRequest;
import com.plaid.client.model.PaymentInitiationPaymentCreateResponse;

PaymentAmount amount = new PaymentAmount()
  .currency(PaymentAmountCurrency.GBP)
  .value(999.99);

PaymentInitiationPaymentCreateRequest request = new PaymentInitiationPaymentCreateRequest()
  .recipientId(recipientId)
  .userId(userId)
  .reference("reference")
  .amount(amount);

Response<PaymentInitiationPaymentCreateResponse> response = client
  .paymentInitiationPaymentCreate(request)
  .execute();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/payment_initiation/payment/create \\
-H 'Content-Type: application/json' \\
-d '{
 "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
 "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
 "recipient_id": String,
 "user_id": String,
 "reference": String,
 "amount": Object,
 "schedule": Object
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`require 'plaid'

request = Plaid::PaymentInitiationPaymentCreateRequest.new(
 {
 recipient_id: recipient_id,
 user_id: user_id,
 reference: "testpayment",
 amount: {
      value: 100.00,
      currency: "GBP"
    }
 }
)
response = client.payment_initiation_payment_create(request)
payment_id = response.payment_id
status = response.status
`}</code></pre>
      {/* prettier-ignore */}
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const { PaymentInitiationPaymentCreateRequest } = require('plaid');

const request: PaymentInitiationPaymentCreateRequest = {
  recipient_id: recipientID,
  user_id: userID,
  reference: 'TestPayment',
  amount: {
    currency: 'GBP',
    value: 100.00,
  },
};
try {
  const response = await plaidClient.paymentInitiationPaymentCreate(request);
  const paymentID = response.data.payment_id;
  const status = response.data.status;
} catch (error) {
  // handle error
}
`}</code></pre>
    </MDXCodeBlock>
    <Callout mdxType="Callout">
      <p>{`Make sure to store the `}<inlineCode parentName="p">{`payment_id`}</inlineCode>{` in your backend together with your own metadata (e.g. your internal `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{`). You can use this later to process the `}<a parentName="p" {...{
          "href": "/docs/api/products/payment-initiation/#webhooks"
        }}>{`payment status updates`}</a></p>
    </Callout>
    <h5 {...{
      "id": "create-a-link_token"
    }}>{`Create a link_token`}</h5>
    <p>{`Before initializing Link, you need to create a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` on the server side of your application.
A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` is a short-lived, one-time use token that is used to authenticate your app with Link.
You can create one using the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint. Then, on the client side of your application, you'll need to initialize Link with the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` that you created. This will bring up the payment initiation flow in Link that allows your end user to confirm the payment.`}</p>
    <p>{`When calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` to create a token for use with Payment Initiation, you provide the `}<inlineCode parentName="p">{`payment_id`}</inlineCode>{` and the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, and specify `}<inlineCode parentName="p">{`payment_initiation`}</inlineCode>{` as the product.`}</p>
    <p>{`The top-level `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` is the Plaid-generated identifier returned by `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{`, and must match the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` you provided when creating the payment. The `}<inlineCode parentName="p">{`user.client_user_id`}</inlineCode>{` value is your own application's identifier for the signed-in user and is primarily used for logging, which is why both are supplied.`}</p>
    <Callout mdxType="Callout">
      <p>{`While it is possible to initialize Link with many products, `}<inlineCode parentName="p">{`payment_initiation`}</inlineCode>{` cannot be specified along with any other products and must be the only product in Link's product array if it is being used.`}</p>
    </Callout>
    <p>{`In the code samples below, you will need to replace `}<inlineCode parentName="p">{`PLAID_CLIENT_ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`PLAID_SECRET`}</inlineCode>{` with your own keys, which you can get from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`Dashboard`}</a>{`.`}</p>
    <SampleCode.CreateLinkTokenPaymentInitiation />
    <p>{`Once you have exposed an endpoint to create a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` in your application server, you now need to configure your client application to import and use Link.`}</p>
    <h4 {...{
      "id": "launch-the-payment-initiation-flow-in-link"
    }}>{`Launch the payment initiation flow in Link`}</h4>
    <p>{`Plaid Link is a drop-in module that provides a secure, elegant authentication flow for the many financial institutions that Plaid supports. Link makes it secure and easy for users to connect their bank accounts to Plaid.
Because Link has access to all the details of the payment at the time of initialisation, it will display a screen with the payment details already populated. All your end user has to do is log in to their financial institution through a Link-initiated OAuth flow, select a funding account, and confirm the payment details.`}</p>
    <Image src='/assets/img/docs/payment_initiation/payment-initiation-uk-link-screens.png' alt="Plaid Link payment flow: View payment details, select bank, authenticate with bank, payment confirmation with bank and transaction info." mdxType="Image" />
    <p>{`Note that these instructions cover Link on the web. For instructions on using Link within mobile apps, see the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{`. If you want to customise Link's look and feel, you can do so from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link"
      }}>{`Dashboard`}</a>{`.`}</p>
    <h5 {...{
      "id": "install-link-dependency"
    }}>{`Install Link dependency`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "#index.html",
        "#index.html": true
      }}>{`<head>
  <title>Link for Payment Initiation</title>
  <script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
</head>
`}</code></pre>
    <h5 {...{
      "id": "configure-the-client-side-link-handler"
    }}>{`Configure the client-side Link handler`}</h5>
    <p>{`Plaid communicates to you certain events that relate to how the user is interacting with Link. What you do with each of these event triggers depends on your particular use case, but a basic scaffolding might look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #app.js",
        "switcher": "false",
        "#app.js": true
      }}>{`const linkHandler = Plaid.create({
  // Create a new link_token to initialize Link
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // Show a success page to your user confirming that the
    // payment will be processed.
    //
    // The 'metadata' object contains info about the institution
    // the user selected.
    // For example:
    //  metadata  = {
    //    link_session_id: "123-abc",
    //    institution: {
    //      institution_id: "ins_117243",
    //      name:"Monzo"
    //    }
    //  }
  },
  onExit: (err, metadata) => {
    // The user exited the Link flow.
    if (err != null) {
      // The user encountered a Plaid API error prior to exiting.
    }
    // 'metadata' contains information about the institution
    // that the user selected and the most recent API request IDs.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect with Plaid.
    // For example:
    //  eventName = "TRANSITION_VIEW",
    //  metadata  = {
    //    link_session_id: "123-abc",
    //    mfa_type:        "questions",
    //    timestamp:       "2017-09-14T14:42:19.350Z",
    //    view_name:       "MFA",
    //  }
  },
});

linkHandler.open();
`}</code></pre>
    <Callout mdxType="Callout">
      <p>{`Unlike other products, for `}<inlineCode parentName="p">{`payment_initiation`}</inlineCode>{` it is not necessary to exchange the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`. You only need the `}<inlineCode parentName="p">{`payment_id`}</inlineCode>{` to interact with the `}<inlineCode parentName="p">{`payment_initiation`}</inlineCode>{` endpoints.`}</p>
    </Callout>
    <h4 {...{
      "id": "verify-payment-status"
    }}>{`Verify payment status`}</h4>
    <h5 {...{
      "id": "listening-for-status-update-webhooks"
    }}><strong parentName="h5">{`Listening for status update webhooks`}</strong></h5>
    <p>{`Once the payment has been authorised by the end user and the Link flow is completed, the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback is invoked, signaling that the payment status is now `}<inlineCode parentName="p">{`PAYMENT_STATUS_AUTHORISING`}</inlineCode>{`.`}</p>
    <p>{`From this point on, you can track the payment status using the `}<a parentName="p" {...{
        "href": "/docs/api/products/payment-initiation/#payment_status_update"
      }}><inlineCode parentName="a">{`PAYMENT_STATUS_UPDATE`}</inlineCode></a>{` webhook that is triggered by Plaid when updates occur:`}</p>
    <ul>
      <li parentName="ul">{`Updates are sent by Plaid to the `}<a parentName="li" {...{
          "href": "/docs/api/link/#link-token-create-request-webhook"
        }}>{`configured webhook URL`}</a>{` to indicate that the status of an initiated payment has changed. All Payment Initiation webhooks have a `}<a parentName="li" {...{
          "href": "/docs/api/products/payment-initiation/#PaymentStatusUpdateWebhook-webhook-type"
        }}><inlineCode parentName="a">{`webhook_type`}</inlineCode></a>{` of `}<inlineCode parentName="li">{`PAYMENT_INITIATION`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Once you receive the status update, use the `}<a parentName="li" {...{
          "href": "/docs/api/products/payment-initiation/#PaymentStatusUpdateWebhook-payment-id"
        }}><inlineCode parentName="a">{`payment_id`}</inlineCode></a>{` field to retrieve the payment's metadata from your database.`}</li>
      <li parentName="ul">{`From the status update object, use the `}<a parentName="li" {...{
          "href": "/docs/api/products/payment-initiation/#PaymentStatusUpdateWebhook-new-payment-status"
        }}><inlineCode parentName="a">{`new_payment_status`}</inlineCode></a>{` field to decide what action needs to be taken for this payment. For example, you may treat `}<inlineCode parentName="li">{`PAYMENT_STATUS_INITIATED`}</inlineCode>{` as your primary success signal, with `}<inlineCode parentName="li">{`PAYMENT_STATUS_EXECUTED`}</inlineCode>{` as supplementary confirmation if and when it arrives, or notify the user that their payment got rejected (`}<inlineCode parentName="li">{`PAYMENT_STATUS_REJECTED`}</inlineCode>{`).`}</li>
    </ul>
    <Callout warning mdxType="Callout">
      <p><inlineCode parentName="p">{`PAYMENT_STATUS_SETTLED`}</inlineCode>{` is the only status that confirms funds have arrived at the destination account, and it is available only via `}<a parentName="p" {...{
          "href": "/docs/payment-initiation/virtual-accounts"
        }}>{`Virtual Accounts`}</a>{`. If your use case requires confirmation of receipt before releasing funds or goods, use Virtual Accounts, or confirm the funds have arrived in your own bank account — see `}<a parentName="p" {...{
          "href": "/docs/payment-initiation/reconciliation/"
        }}>{`Reconciliation`}</a>{` for how to match an arriving credit back to the payment. For details on what each status does and does not guarantee, see `}<a parentName="p" {...{
          "href": "/docs/payment-initiation/payment-status/"
        }}>{`Payment Status`}</a>{`.`}</p>
    </Callout>
    <p>{`For more information, see `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/payment-status/"
      }}>{`Payment Status`}</a>{`.`}</p>
    <p>{`For more information on how to implement your webhook listener, see the `}<a parentName="p" {...{
        "href": "/docs/api/webhooks/"
      }}>{`webhooks documentation`}</a>{`.`}</p>
    <h4 {...{
      "id": "sample-code-in-plaid-pattern"
    }}>{`Sample code in Plaid Pattern`}</h4>
    <p>{`For an example implementation of Payment Initiation, see the `}<a parentName="p" {...{
        "href": "https://github.com/plaid/payment-initiation-pattern-app"
      }}>{`Payment Initiation Pattern App`}</a>{` on GitHub, which uses Payment Initiation in an account funding use case.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "get-plaid-api-keys-and-complete-application-and-company-profile",
        level: 4,
        title: "Get Plaid API keys and complete application and company profile",
      },
{
        id: "access-payment-initiation",
        level: 4,
        title: "Access Payment Initiation",
      },
{
        id: "install-and-initialize-plaid-libraries",
        level: 4,
        title: "Install and initialize Plaid libraries",
      },
{
        id: "one-time-payment-initiation-flow-overview",
        level: 4,
        title: "One-time Payment Initiation flow overview",
      },
{
        id: "launch-the-payment-initiation-flow-in-link",
        level: 4,
        title: "Launch the payment initiation flow in Link",
      },
{
        id: "verify-payment-status",
        level: 4,
        title: "Verify payment status",
      },
{
        id: "sample-code-in-plaid-pattern",
        level: 4,
        title: "Sample code in Plaid Pattern",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
